import { isBlackOpening, loadedBlackCourse } from "../content/blackCourses.ts";
import { Chess } from "@vendor/chess.js";
import type { BlackTheme } from "../content/black/types.ts";
import { whiteCoachingPack } from "../content/whiteCoachingPacks.ts";
import {
  clean,
  donePendingMissed,
  type ThemeContext,
  type ThemeState,
} from "../content/themeDetection.ts";

/**
 * Course themes: the handful of ideas each opening is built around. The
 * trainer tracks whether the student has realized each idea on the actual
 * board, so coaching can connect moves to one another ("this completes the
 * knight tour") instead of judging every move in isolation.
 *
 * Detection uses the moves White has played plus the current board. It is a
 * teaching ledger, not a claim about theory: a theme marked "missed" means
 * the course's version of the idea is no longer available in this game.
 */

export type { CourseTheme, ThemeContext, ThemeState } from "../content/themeDetection.ts";

export type ThemeStatus = {
  id: string;
  title: string;
  idea: string;
  mainlineMove: number;
  state: ThemeState;
};

export function themeContext(
  allSans: string[],
  fen: string,
  counts: (index: number) => boolean = () => true
): ThemeContext {
  // A move that does not count (played away from the course) is kept as a
  // placeholder so move indexes still line up.
  const all = allSans.map((san, index) => (counts(index) ? clean(san) : "~"));
  const white = all.filter((_, index) => index % 2 === 0);
  const castled = allSans.some((san, index) => index % 2 === 0 && san.startsWith("O-O"));
  const playedWhite = allSans.filter((_, index) => index % 2 === 0).map(clean);
  return { white, all, chess: new Chess(fen), castled, played: playedWhite };
}

/**
 * Whether the piece a theme move needs is still on the board: a bishop of the
 * destination square's color, a knight, a rook or the queen. Pawn and king
 * moves are always treated as available.
 */
function pieceStillAvailable(chess: Chess, color: "w" | "b", san: string): boolean {
  const type = san[0];
  if (!"NBRQ".includes(type)) return true;
  const target = san.replace(/[+#=].*$/, "").slice(-2);
  const light = (target.charCodeAt(0) - 97 + Number(target[1])) % 2 === 0;
  return chess.board().some((rank, row) =>
    rank.some((piece, file) => {
      if (!piece || piece.color !== color || piece.type !== type.toLowerCase()) return false;
      if (type !== "B") return true;
      return ((file + (8 - row)) % 2 === 0) === light;
    })
  );
}

/** Themes written as data (Black courses, and White courses with a coaching pack). */
function dataThemeStatuses(
  themes: BlackTheme[],
  color: "w" | "b",
  allSans: string[],
  fen: string,
  counts: (index: number) => boolean
): ThemeStatus[] {
  const parity = color === "w" ? 0 : 1;
  const mine = allSans
    .map((san, i) => (counts(i) ? clean(san) : "~"))
    .filter((_, i) => i % 2 === parity);
  const playedFrom: { san: string; from: string }[] = [];
  if (themes.some((theme) => theme.from)) {
    const board = new Chess();
    for (const [ply, san] of allSans.entries()) {
      try {
        const move = board.move(san);
        if (!move) break;
        if (ply % 2 === parity) playedFrom.push({ san: clean(move.san), from: move.from });
      } catch {
        break;
      }
    }
  }
  // Castling counts however it was reached: the king is safe either way.
  const castled = allSans.some((san, index) => index % 2 === parity && san.startsWith("O-O"));
  const rights = (fen.split(" ")[2] || "").replace(color === "w" ? /[kq]/g : /[KQ]/g, "");
  return themes.map((theme) => {
    let index = -1;
    const ordered = (theme.ordered ? theme.allOf || [] : []).every((san) => {
      index = mine.indexOf(san, index + 1);
      return index >= 0;
    });
    const done =
      (theme.anyOf ? theme.anyOf.some((san) => mine.includes(san)) : true) &&
      (theme.allOf ? theme.allOf.every((san) => mine.includes(san)) : true) &&
      ordered &&
      (!theme.from ||
        playedFrom.some((move) => move.from === theme.from && theme.anyOf?.includes(move.san))) &&
      (theme.castled ? castled : true);
    const board = new Chess(fen);
    const pieceGone = [...(theme.anyOf || []), ...(theme.allOf || [])].some(
      (san) => !mine.includes(san) && !pieceStillAvailable(board, color, san)
    );
    const missed =
      (Boolean(theme.castled) && !castled && !/[kqKQ]/.test(rights)) || (!done && pieceGone);
    return {
      id: theme.id,
      title: theme.title,
      idea: theme.idea,
      mainlineMove: theme.mainlineMove,
      state: donePendingMissed(done, missed),
    };
  });
}

/**
 * The course ideas and whether each is done, still possible or missed.
 * `counts` says whether the move at a history index counts toward an idea:
 * the session passes false for the student's moves that were not course
 * moves, so a blunder or a move from another line never realizes one.
 */
export function themeStatuses(
  openingId: string | null | undefined,
  allSans: string[],
  fen: string,
  counts: (index: number) => boolean = () => true
): ThemeStatus[] {
  if (isBlackOpening(openingId)) {
    return dataThemeStatuses(loadedBlackCourse(openingId)?.themes || [], "b", allSans, fen, counts);
  }
  const pack = whiteCoachingPack(openingId);
  if (!pack) return [];
  const themes = pack.codeThemes;
  if (!themes) return dataThemeStatuses(pack.themes, "w", allSans, fen, counts);
  const ctx = themeContext(allSans, fen, counts);
  return themes.map((theme) => ({
    id: theme.id,
    title: theme.title,
    idea: theme.idea,
    mainlineMove: theme.mainlineMove,
    state: theme.detect(ctx),
  }));
}

export function newlyAchievedThemes(before: ThemeStatus[], after: ThemeStatus[]): ThemeStatus[] {
  const wasDone = new Set(before.filter((t) => t.state === "done").map((t) => t.id));
  return after.filter((t) => t.state === "done" && !wasDone.has(t.id));
}

export function themeSummary(statuses: ThemeStatus[]): {
  done: number;
  total: number;
  pending: ThemeStatus[];
  missed: ThemeStatus[];
  achieved: ThemeStatus[];
} {
  return {
    done: statuses.filter((t) => t.state === "done").length,
    total: statuses.length,
    achieved: statuses.filter((t) => t.state === "done"),
    pending: statuses.filter((t) => t.state === "pending"),
    missed: statuses.filter((t) => t.state === "missed"),
  };
}
